import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import apiClient from "../api/client";
import { Report } from "../types";
import DataTable from "../components/DataTable";
import ExportButton from "../components/ExportButton";
import RegionBadge from "../components/RegionBadge";
import ShowPersonalToggle from "../components/ShowPersonalToggle";
import { useRoles } from "../auth/access";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";

export default function ReportsPage() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const { workspaceId } = useParams<{ workspaceId?: string }>();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Personal workspaces are excluded from the aggregate view by default.
  const [showPersonal, setShowPersonal] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { canReach, isMon } = useRoles();

  const columns: ColumnDef<Report, any>[] = [
    { accessorKey: "name", header: "Name" },
    // Only in the aggregate view: inside a single workspace every row holds
    // the same value, and a column of one repeated name is just noise.
    ...(workspaceId
      ? []
      : [
          {
            id: "workspace",
            header: "Workspace",
            // Sorts and searches on the name the reader can see, falling back
            // to the id so a workspace the collector has not named yet still
            // sorts somewhere predictable rather than into a blank block.
            accessorFn: (row: Report) => row.workspaceName || row.workspaceId,
            cell: ({ row }: { row: { original: Report } }) => {
              const { workspaceId: id, workspaceName } = row.original;
              if (!id) return <span className="text-gray-400">—</span>;
              return (
                <button
                  // The row itself opens the report, so this click has to stop
                  // there or the workspace link would never be reachable.
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/workspaces/${id}`);
                  }}
                  className="text-left text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                  title={`Open workspace ${workspaceName || id}`}
                >
                  {workspaceName || <span className="font-mono text-xs">{id}</span>}
                </button>
              );
            },
          } as ColumnDef<Report, any>,
        ]),
    { accessorKey: "createdDateTime", header: "Created", cell: ({ getValue }) => formatDate(getValue()) },
    { accessorKey: "modifiedDateTime", header: "Last Modified", cell: ({ getValue }) => formatDate(getValue()) },
    {
      accessorKey: "datasetId",
      header: "Dataset ID",
      cell: ({ getValue }) => {
        const v = getValue<string>();
        return v ? <span className="font-mono text-xs">{v}</span> : "—";
      },
    },
    {
      id: "region",
      header: "Region",
      accessorFn: (row) => row.region || "",
      // A report has no region of its own; this is the geography of the
      // workspace that holds it, joined on server-side.
      cell: ({ row }) => <RegionBadge region={row.original.region} source="capacity" />,
    },
    { accessorKey: "lastSyncedAt", header: "Last Synced", cell: ({ getValue }) => formatDate(getValue()) },
  ];

  useEffect(() => {
    setLoading(true);
    setError(null);

    // One request either way. The aggregate view used to fetch every
    // workspace and then issue a further request per workspace for its
    // reports — around 135 with personal workspaces hidden, and about 4,100
    // with them shown — to build a list the API can return in one scan.
    const request = workspaceId
      ? apiClient.get<Report[]>(`/api/workspaces/${workspaceId}/reports`)
      : apiClient.get<Report[]>("/api/reports", {
          params: showPersonal ? { include_personal: "true" } : {},
        });

    request
      .then((res) => setReports(res.data))
      .catch((err) => setError(err.message ?? "Failed to load reports."))
      .finally(() => setLoading(false));
  }, [workspaceId, showPersonal]);

  const query = search.trim().toLowerCase();
  // The workspace name is searchable because it is now on screen: a column a
  // reader can see but not search for reads as a broken search box.
  const filtered = query
    ? reports.filter((r) =>
        [r.name, r.reportId, r.datasetId, r.workspaceId, r.workspaceName].some((field) =>
          (field || "").toLowerCase().includes(query)
        )
      )
    : reports;

  const title = workspaceId ? "Reports in Workspace" : "All Reports";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            {workspaceId && (
              <>
                <button
                  onClick={() => navigate("/workspaces")}
                  className="hover:text-blue-600 transition-colors"
                >
                  Workspaces
                </button>
                <span>/</span>
                <span className="font-mono text-xs">{workspaceId}</span>
                <span>/</span>
              </>
            )}
            <span className="text-gray-700 font-medium">Reports</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Click a report row to view its data sources and change history.
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {!workspaceId && (
            <ShowPersonalToggle show={showPersonal} onChange={setShowPersonal} />
          )}
          {/* The export endpoint is not part of the monitored-only role's
              surface, so for that role the button could only ever 403. */}
          {!isMon && (
            <ExportButton
              endpoint="/api/export/reports"
              params={workspaceId ? { workspace_id: workspaceId } : {}}
              filename="reports.csv"
            />
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading reports…
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && reports.length > 0 && (
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search reports…"
            className="pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      )}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-500">
            {query ? (
              <>
                <span className="font-medium text-gray-700">{filtered.length}</span> of{" "}
                <span className="font-medium text-gray-700">{reports.length}</span> report
                {reports.length !== 1 ? "s" : ""} match.
              </>
            ) : (
              <>
                {reports.length} report{reports.length !== 1 ? "s" : ""} found.
              </>
            )}
          </p>
          {reports.length === 0 ? (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-5 py-6 text-sm text-yellow-800 space-y-1">
              <p className="font-medium">
                {workspaceId
                  ? "No reports collected yet for this workspace."
                  : "No reports collected yet."}
              </p>
              <p className="text-yellow-700">
                The workspace collector must run successfully before reports appear here.
                {canReach("/info") ? (
                  <>
                    {" "}Go to{" "}
                    <button className="underline" onClick={() => navigate("/info")}>
                      System Info
                    </button>{" "}
                    to check collector status and trigger a manual run.
                  </>
                ) : (
                  " Ask an administrator to check collector status on the System Status page."
                )}
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-5 py-6 text-sm text-gray-600">
              No report matches “{search.trim()}”.
            </div>
          ) : (
            <DataTable
              data={filtered}
              columns={columns}
              onRowClick={(r) =>
                navigate(
                  `/workspaces/${r.workspaceId}/artifacts/report/${encodeURIComponent(r.reportId)}`
                )
              }
            />
          )}
        </>
      )}
    </div>
  );
}
